import type {
  InputVariableDefinitions,
  QueryInputVariableDefinition,
  QueryTemplatePredefinedVariable,
  QueryTemplateValueType,
} from '../types/data-connection-queries.js';
import type { TemplateVariableGroup, TemplateVariableGroupKey } from './prompt-template-variables.js';
import {
  isPromptTemplateVariableSupportedInContext,
  PROMPT_TEMPLATE_VARIABLE_GROUPS,
  PROMPT_TEMPLATE_VARIABLES,
} from './prompt-template-variables.js';

export type SqlTemplateRuntimeVariable = {
  mappedVariable: QueryTemplatePredefinedVariable;
  placeholderAlias: string;
  aliases: string[];
  type: QueryTemplateValueType;
  description: string;
  group: TemplateVariableGroupKey;
};

const USER_METADATA_DYNAMIC_TEMPLATE_KEY = 'user.metadata.[prop_name]';
const USER_METADATA_KEY_PREFIX = 'user.metadata.';

const SQL_RUNTIME_VARIABLE_TYPE_OVERRIDES: Record<string, QueryTemplateValueType> = {
  'brand.isNation': 'boolean',
  'brand.productBrands.names': 'array',
  'brand.drugClasses.names': 'array',
  'brand.genericDrugs.names': 'array',
  'resolvedEntities.productBrand.id': 'array',
  'resolvedEntities.productBrand.name': 'array',
  'resolvedEntities.indication.id': 'array',
  'resolvedEntities.indication.name': 'array',
};

const SQL_TEMPLATE_PLACEHOLDER_PATTERN = /\$\{([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*)\}/g;

const DYNAMIC_USER_METADATA_RUNTIME_VARIABLE: SqlTemplateRuntimeVariable = {
  mappedVariable: USER_METADATA_DYNAMIC_TEMPLATE_KEY,
  placeholderAlias: 'user.metadata.<prop_name>',
  aliases: [],
  type: 'string',
  description:
    PROMPT_TEMPLATE_VARIABLES.find((variable) => variable.key === USER_METADATA_DYNAMIC_TEMPLATE_KEY)
      ?.description ?? 'A dynamic user metadata value by property name.',
  group: 'user',
};

type SqlApplicableStaticPromptVariable = Exclude<
  (typeof PROMPT_TEMPLATE_VARIABLES)[number],
  { key: typeof USER_METADATA_DYNAMIC_TEMPLATE_KEY }
>;

function isSqlApplicableStaticPromptVariable(
  variable: (typeof PROMPT_TEMPLATE_VARIABLES)[number]
): variable is SqlApplicableStaticPromptVariable {
  if (variable.key === USER_METADATA_DYNAMIC_TEMPLATE_KEY) {
    return false;
  }
  return isPromptTemplateVariableSupportedInContext({ variable, context: 'sql_template' });
}

const STATIC_SQL_TEMPLATE_RUNTIME_VARIABLES: SqlTemplateRuntimeVariable[] = PROMPT_TEMPLATE_VARIABLES.filter(
  isSqlApplicableStaticPromptVariable
).map((variable) => ({
  mappedVariable: variable.key,
  placeholderAlias: variable.key,
  aliases: [],
  type: SQL_RUNTIME_VARIABLE_TYPE_OVERRIDES[variable.key] ?? 'string',
  description: variable.description,
  group: variable.group,
}));

export const SQL_TEMPLATE_RUNTIME_VARIABLES: SqlTemplateRuntimeVariable[] = [
  ...STATIC_SQL_TEMPLATE_RUNTIME_VARIABLES,
  DYNAMIC_USER_METADATA_RUNTIME_VARIABLE,
];

export type SqlTemplateVariableGrouped = {
  group: TemplateVariableGroup;
  variables: SqlTemplateRuntimeVariable[];
};

export function getSqlTemplateVariablesGrouped(): SqlTemplateVariableGrouped[] {
  return PROMPT_TEMPLATE_VARIABLE_GROUPS.map((group) => ({
    group,
    variables: SQL_TEMPLATE_RUNTIME_VARIABLES.filter((variable) => variable.group === group.key),
  })).filter((entry) => entry.variables.length > 0);
}

const SQL_TEMPLATE_ALIAS_TO_PREDEFINED_VARIABLE = SQL_TEMPLATE_RUNTIME_VARIABLES.reduce<
  Partial<Record<string, QueryTemplatePredefinedVariable>>
>((accumulator, variable) => {
  if (variable.placeholderAlias === USER_METADATA_DYNAMIC_TEMPLATE_KEY) {
    return accumulator;
  }

  accumulator[variable.placeholderAlias] = variable.mappedVariable;

  for (const alias of variable.aliases) {
    accumulator[alias] = variable.mappedVariable;
  }

  return accumulator;
}, {});

const SQL_TEMPLATE_VARIABLE_BY_MAPPED = STATIC_SQL_TEMPLATE_RUNTIME_VARIABLES.reduce<
  Partial<Record<QueryTemplatePredefinedVariable, SqlTemplateRuntimeVariable>>
>(
  (accumulator, variable) => {
    accumulator[variable.mappedVariable] = variable;
    return accumulator;
  },
  {} as Partial<Record<QueryTemplatePredefinedVariable, SqlTemplateRuntimeVariable>>
);

function isDynamicUserMetadataPlaceholderKey(
  placeholderKey: string
): placeholderKey is `user.metadata.${string}` {
  return (
    placeholderKey.startsWith(USER_METADATA_KEY_PREFIX) &&
    placeholderKey.length > USER_METADATA_KEY_PREFIX.length
  );
}

function resolveMappedVariableFromPlaceholder(
  placeholderKey: string
): QueryTemplatePredefinedVariable | undefined {
  const staticMappedVariable = SQL_TEMPLATE_ALIAS_TO_PREDEFINED_VARIABLE[placeholderKey];
  if (staticMappedVariable !== undefined) {
    return staticMappedVariable;
  }

  if (isDynamicUserMetadataPlaceholderKey(placeholderKey)) {
    return placeholderKey;
  }

  return undefined;
}

function getRuntimeVariableByMappedVariable(
  mappedVariable: QueryTemplatePredefinedVariable
): SqlTemplateRuntimeVariable | undefined {
  const staticRuntimeVariable = SQL_TEMPLATE_VARIABLE_BY_MAPPED[mappedVariable];
  if (staticRuntimeVariable !== undefined) {
    return staticRuntimeVariable;
  }

  if (isDynamicUserMetadataPlaceholderKey(mappedVariable)) {
    return DYNAMIC_USER_METADATA_RUNTIME_VARIABLE;
  }

  return undefined;
}

export function extractSqlTemplatePlaceholderKeys(templateSql: string): string[] {
  const variableMatches = templateSql.matchAll(SQL_TEMPLATE_PLACEHOLDER_PATTERN);
  const uniqueKeys = new Set<string>();

  for (const match of variableMatches) {
    const variableKey = match[1];
    if (variableKey) {
      uniqueKeys.add(variableKey);
    }
  }

  return Array.from(uniqueKeys);
}

export function getSupportedSqlTemplatePlaceholderAliases(): string[] {
  return SQL_TEMPLATE_RUNTIME_VARIABLES.flatMap((variable) => [
    variable.placeholderAlias,
    ...variable.aliases,
  ]);
}

export function deriveSqlTemplateInputVariableDefinitions(data: {
  templateSql: string;
  existingDefinitions?: InputVariableDefinitions;
}): {
  placeholderKeys: string[];
  unknownPlaceholderKeys: string[];
  inputVariableDefinitions: InputVariableDefinitions;
} {
  const placeholderKeys = extractSqlTemplatePlaceholderKeys(data.templateSql);
  const inputVariableDefinitions: InputVariableDefinitions = {};
  const unknownPlaceholderKeys: string[] = [];

  for (const placeholderKey of placeholderKeys) {
    const previousDefinition = data.existingDefinitions?.[placeholderKey];
    const inferredMappedVariable = resolveMappedVariableFromPlaceholder(placeholderKey);
    const mappedVariable = previousDefinition?.mappedVariable ?? inferredMappedVariable;

    if (previousDefinition) {
      const shouldBackfillMappedVariable =
        previousDefinition.mappedVariable === undefined &&
        previousDefinition.source !== 'llm_only' &&
        inferredMappedVariable !== undefined;

      inputVariableDefinitions[placeholderKey] = {
        ...previousDefinition,
        name: placeholderKey,
        ...(shouldBackfillMappedVariable ? { mappedVariable: inferredMappedVariable } : {}),
      };

      if (
        inputVariableDefinitions[placeholderKey].source !== 'llm_only' &&
        inputVariableDefinitions[placeholderKey].mappedVariable === undefined
      ) {
        unknownPlaceholderKeys.push(placeholderKey);
      }

      continue;
    }

    const definition = buildDefaultDefinition(placeholderKey, mappedVariable);

    if (mappedVariable === undefined && definition.source !== 'llm_only') {
      unknownPlaceholderKeys.push(placeholderKey);
    }

    inputVariableDefinitions[placeholderKey] = definition;
  }

  return {
    placeholderKeys,
    unknownPlaceholderKeys,
    inputVariableDefinitions,
  };
}

function buildDefaultDefinition(
  placeholderKey: string,
  mappedVariable: QueryTemplatePredefinedVariable | undefined
): QueryInputVariableDefinition {
  if (mappedVariable) {
    const runtimeVariable = getRuntimeVariableByMappedVariable(mappedVariable);
    const runtimeVariableType = runtimeVariable?.type ?? 'string';
    const runtimeVariableDescription = isDynamicUserMetadataPlaceholderKey(mappedVariable)
      ? `Runtime user metadata value for ${mappedVariable}`
      : runtimeVariable?.description;

    return {
      name: placeholderKey,
      source: 'runtime_only',
      type: runtimeVariableType,
      mappedVariable,
      description: runtimeVariableDescription,
      required: true,
    };
  }

  return {
    name: placeholderKey,
    source: 'llm_only',
    type: 'string',
    description: `LLM-supplied input for ${placeholderKey}`,
    required: true,
  };
}
